import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

function toLocalInput(d) {
  const dt = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return dt.toISOString().slice(0, 16);
}

export default function MaintenancePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user, ['admin', 'tech']);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', service_ids: [],
    starts_at: toLocalInput(new Date()),
    ends_at: toLocalInput(new Date(Date.now() + 60 * 60 * 1000)),
  });

  const load = async () => {
    const [{ data: ms }, { data: svcs }] = await Promise.all([api.get('/maintenance'), api.get('/services')]);
    setItems(ms); setServices(svcs);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post('/maintenance', {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      });
      toast.success(t('success'));
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
  };

  const remove = async (id) => {
    if (!window.confirm('Confirm?')) return;
    await api.delete(`/maintenance/${id}`); load();
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id],
    }));
  };

  const upcoming = items.filter((i) => new Date(i.starts_at) >= new Date());
  const past = items.filter((i) => new Date(i.starts_at) < new Date());

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('maintenance_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{upcoming.length} {t('upcoming_maintenance').toLowerCase()}</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="maintenance-create-button"><Plus className="h-4 w-4 mr-2" /> {t('maintenance_new')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t('maintenance_new')}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="maintenance-form-title" /></div>
                <div className="space-y-1"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label>{t('maintenance_starts')}</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></div>
                  <div className="space-y-1"><Label>{t('maintenance_ends')}</Label><Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></div>
                </div>
                <div>
                  <Label className="mb-2 block">{t('maintenance_services')}</Label>
                  <div className="flex flex-wrap gap-2">
                    {services.map((s) => (
                      <button key={s.id} type="button" onClick={() => toggleService(s.id)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-150 ${form.service_ids.includes(s.id) ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.1)] text-[hsl(var(--primary))]' : 'border-border text-[hsl(var(--muted-foreground))]'}`}>
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button><Button onClick={create} data-testid="maintenance-form-submit">{t('create')}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">{t('upcoming_maintenance')}</h2>
        {upcoming.length === 0 ? <div className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">{t('no_data')}</div> :
          <ul className="divide-y divide-border">
            {upcoming.map((m) => (
              <li key={m.id} className="py-3 flex items-start gap-3" data-testid={`maintenance-row-${m.id}`}>
                <span className="mt-1 h-2 w-2 rounded-full bg-sky-400" />
                <div className="flex-1">
                  <div className="font-medium">{m.title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{m.description}</div>
                  <div className="text-xs font-mono text-[hsl(var(--muted-foreground))] mt-1">
                    {new Date(m.starts_at).toLocaleString()} → {new Date(m.ends_at).toLocaleString()}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(m.service_ids || []).map((sid) => {
                      const s = services.find((x) => x.id === sid);
                      return s ? <span key={sid} className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--muted))]">{s.name}</span> : null;
                    })}
                  </div>
                </div>
                {canEdit && <Button variant="ghost" size="sm" onClick={() => remove(m.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>}
              </li>
            ))}
          </ul>
        }
      </Card>

      <Card className="p-5 bg-card border border-border">
        <h2 className="font-display text-lg font-semibold mb-3">Past</h2>
        {past.length === 0 ? <div className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">{t('no_data')}</div> :
          <ul className="divide-y divide-border">
            {past.slice(0, 20).map((m) => (
              <li key={m.id} className="py-3 flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
                <div className="flex-1"><div className="text-sm">{m.title}</div></div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{m.starts_at ? formatDistanceToNow(new Date(m.starts_at), { addSuffix: true }) : ''}</span>
              </li>
            ))}
          </ul>
        }
      </Card>
    </div>
  );
}
